import React, { Fragment, useContext, useEffect, useState } from 'react';
import * as R from 'ramda';
import { Box, Button, CircularProgress, Drawer } from '@mui/material';
import { DrawerLayoutWrapper } from 'common';
import { bool, func, number } from 'prop-types';

import { formatNumberWithoutDecimals } from 'utils/helpers/format';
import { useUserRolePermissions } from 'hooks';
import { createSnack } from 'snackbar/snackbarSlice';
import { useDispatch } from 'react-redux';
import { AuthContext } from 'authentication/AuthContext';
import Tooltip from '@mui/material/Tooltip';
import DialogExplicitConfirmation from 'common/dialog-explicit-confirmation/DialogExplicitConfirmation';
import DialogUnsavedChanges from '../components/DialogUnsavedChanges';

import {
  useAcceptDistributionMutation,
  useDeleteDistributionMutation,
  useEditDistributionQuery,
  useRejectDistributionMutation,
  useSaveDistributionMutation,
  useViewDistributionQuery,
} from '../batchesEndpoints';

import { OLD_STATUS_CHIP } from '../components/OldStatusChip';
import StatusChip from '../components/StatusChip';
import { distributionStatusTooltips, formatDate, isEditableStatus } from '../utils';
import DataDisplayItem from '../components/DataDisplayItem';
import EditDistribution from '../edit/EditDistribution';

export const DISTRIBUTIONS_DRAWER_MODE = { EDIT: true, VIEW: false };

const renderChaining = chaining =>
  !R.length(chaining)
    ? null
    : chaining.map((recipient, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={recipient + i}>
          {!!i && <br />}
          {recipient}
        </Fragment>
      ));

const DistributionsDrawer = ({
  distributionId,
  mode = DISTRIBUTIONS_DRAWER_MODE.VIEW,
  refreshBatchData,
  refreshShipmentData,
  setSelectedDistribution,
}) => {
  const [initialState, setInitialState] = useState({});

  // TODO: nothing is settings these?
  // eslint-disable-next-line no-unused-vars
  const [totalProposedVolume, setTotalProposedVolume] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [availableVolumeExceeded, setAvailableVolumeExceeded] = useState(false);

  const isEditMode = mode === DISTRIBUTIONS_DRAWER_MODE.EDIT;
  const [previousDistributionMode, setPreviousDistributionMode] = useState(null);

  const { data: distributionQuery = {}, isFetching } = useViewDistributionQuery(
    {
      params: { distributionId },
    },
    {
      skip: !distributionId,
    }
  );

  const { isProductShippingManage } = useUserRolePermissions();
  const { affiliates } = useContext(AuthContext);

  const [confirmationDialog, setConfirmationDialog] = useState();
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [unsavedDialog, setUnsavedDialog] = useState();

  const [editState, setEditState] = useState({});

  const [saveDistribution] = useSaveDistributionMutation();
  const [deleteDistribution] = useDeleteDistributionMutation();
  const [acceptDistribution] = useAcceptDistributionMutation();
  const [rejectDistribution] = useRejectDistributionMutation();

  const dispatch = useDispatch();

  const distributionAffiliate = affiliates.find(
    ({ customerNameShort }) => customerNameShort === distributionQuery?.inventoryOwnerName
  );

  const shipperAffiliate = affiliates.find(
    ({ customerNameShort }) => customerNameShort === distributionQuery?.shipperName
  );

  const hasAcceptRejectPermissions = R.all(R.equals(true), [
    !isFetching,
    isProductShippingManage,
    isEditableStatus(distributionQuery.status),
    !!distributionAffiliate,
  ]);

  const hasEditPermissions = R.all(R.equals(true), [
    !isFetching,
    isProductShippingManage,
    isEditableStatus(distributionQuery?.status),
    !!shipperAffiliate,
  ]);

  const hasDeletePermissions = R.all(R.equals(true), [
    !isFetching,
    isProductShippingManage,
    isEditableStatus(distributionQuery?.status),
    !!shipperAffiliate,
  ]);

  const {
    data: editDistributionQuery = {},
    isFetching: isFetchingEditQuery,
    isSuccess: isEditQuerySuccess,
  } = useEditDistributionQuery(
    { params: { distributionId } },
    { refetchOnMountOrArgChange: true, skip: !distributionId || !hasEditPermissions }
  );

  const editDistributionProp = R.prop(R.__, editDistributionQuery);

  useEffect(() => {
    if (isEditQuerySuccess) {
      const newState = R.mergeLeft({
        chainRecipients:
          editDistributionProp('chainRecipients').map(
            ({ chainContactAffiliate, inventoryOwnerName: companyCustomerName } = {}) => ({
              companyCustomerName,
              ...chainContactAffiliate,
            })
          ) || [],
        location: {
          id: editDistributionProp('locationId'),
          locationName: editDistributionProp('locationName'),
        },
        reconsignmentControl: editDistributionProp('reconsignmentControl'),
        shipmentChain: editDistributionProp('shipmentChain'),
        volume: formatNumberWithoutDecimals(editDistributionProp('volume') || 0),
      });
      setEditState(newState);
      setInitialState(newState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditQuerySuccess, editDistributionQuery]);

  const initialValidationState = {
    chainRecipients: '',
    location: '',
    volume: '',
  };

  const [validationMessages, setValidationMessages] = useState(initialValidationState);

  const showSnack = ({ error, data: { message } = {} }) =>
    dispatch(
      createSnack(
        error
          ? {
              message: error.data.title,
              options: { variant: 'error' },
            }
          : { message }
      )
    );

  const hasChanges = !R.equals(editState, initialState);

  const handleEditDistribution = () => {
    setPreviousDistributionMode('VIEW');
    setSelectedDistribution({ distributionId, mode: DISTRIBUTIONS_DRAWER_MODE.EDIT });
  };

  const handleEditDistributionClose = () => {
    const leftButtonAction = () => {
      if (isEditMode && previousDistributionMode !== 'VIEW') {
        setSelectedDistribution(null);
      } else {
        setPreviousDistributionMode(null);
        setSelectedDistribution({ distributionId, mode: DISTRIBUTIONS_DRAWER_MODE.VIEW });
      }
      setUnsavedDialog();
    };

    hasChanges
      ? setUnsavedDialog({
          leftButtonAction,
          rightButtonAction: () => setUnsavedDialog(),
        })
      : leftButtonAction();
  };
  const onClose = () => {
    if (hasChanges) {
      setUnsavedDialog({
        leftButtonAction: () => {
          setUnsavedDialog();
          setSelectedDistribution();
          setValidationMessages(initialValidationState);
          setInitialState({});
          setEditState({});
        },
        rightButtonAction: () => setUnsavedDialog(),
      });
    } else {
      setSelectedDistribution();
      setValidationMessages(initialValidationState);
      setInitialState({});
      setEditState({});
    }
  };

  const handleEditDistributionSave = () =>
    saveDistribution({
      body: {
        chainRecipients: editState.chainRecipients,
        distributionId,
        locationId: parseInt(editState.location.id),
        reconsignmentControl: !!editState.reconsignmentControl,
        shipmentChain: !!editState.shipmentChain,
        volume: parseInt((editState.volume || '0').replace(',', '')),
      },
    })
      .then(showSnack)
      .then(() => {
        setSelectedDistribution({ distributionId, mode: DISTRIBUTIONS_DRAWER_MODE.VIEW });
        refreshBatchData();
      });

  const handleDeleteDistribution = () =>
    deleteDistribution({
      body: {
        distributionId,
      },
    })
      .then(showSnack)
      .then(() => {
        onClose();
        refreshShipmentData();
      });

  const handleAcceptDistribution = () =>
    acceptDistribution({
      body: {
        dataCompanyId: distributionAffiliate.dataCompanyId,
        distributionId,
      },
    }).then(response => {
      showSnack(response);
      // Enable fields after accepting
      refreshBatchData();
    });

  const shouldDisableFields =
    hasEditPermissions &&
    hasAcceptRejectPermissions &&
    distributionQuery?.status === OLD_STATUS_CHIP.UNCONFIRMED;

  const handleRejectDistribution = () =>
    rejectDistribution({
      body: {
        distributionId,
      },
    }).then(showSnack);

  const isInvalid = [
    !hasChanges,
    R.isNil(editState),
    R.not(R.isEmpty(validationMessages.chainRecipients)),
    R.not(R.isEmpty(validationMessages.location)),
    R.not(R.isEmpty(validationMessages.volume)),
    R.and(editState.shipmentChain, R.isEmpty(editState.chainRecipients)),
  ].includes(true);

  const renderViewButtons = () => (
    <Box
      sx={{
        '> *': { flex: 1 },
        '> :nth-of-type(2)': { ml: 4 },
        display: 'flex',
        width: '100%',
      }}
    >
      {hasEditPermissions &&
      hasAcceptRejectPermissions &&
      !isEditMode &&
      distributionQuery?.status === OLD_STATUS_CHIP.UNCONFIRMED ? (
        <Button
          onClick={() => {
            // Standard dialog
            setConfirmationDialog({
              confirmationMessage: `Are you sure you would like to accept distribution #${distributionId}?`,
              onConfirmAction: handleAcceptDistribution,
              title: 'Accept distribution?',
            });
            setConfirmationDialogOpen(true);
          }}
          variant="contained"
        >
          Accept
        </Button>
      ) : (
        <>
          {hasDeletePermissions && (
            <Button
              onClick={() => {
                // Explicit dialog requiring "DELETE" to be typed in
                setConfirmationDialog({
                  confirmationMessage:
                    'If you still want to delete the distribution, enter the word below and click Confirm.',
                  confirmationValue: 'DELETE',
                  label: 'DELETE',
                  onConfirmAction: handleDeleteDistribution,
                  title: 'Are you sure you want to delete this distribution?',
                  warningList: [
                    'Once deleted, this distribution will be removed permanently and cannot be undone.',
                    'This action cannot be undone.',
                  ],
                });
                setConfirmationDialogOpen(true);
              }}
              variant="outlinedError"
            >
              Delete Distribution
            </Button>
          )}
          {hasEditPermissions && !isEditMode && (
            <Button onClick={handleEditDistribution}>Edit</Button>
          )}
        </>
      )}
      {hasAcceptRejectPermissions &&
        !hasEditPermissions &&
        (distributionQuery?.status === OLD_STATUS_CHIP.UNCONFIRMED ? (
          <Button
            onClick={() => {
              // Standard dialog
              setConfirmationDialog({
                confirmationMessage: `Are you sure you would like to accept distribution #${distributionId}?`,
                onConfirmAction: handleAcceptDistribution,
                title: 'Accept distribution?',
              });
              setConfirmationDialogOpen(true);
            }}
          >
            Accept
          </Button>
        ) : (
          <Button
            onClick={() => {
              // Explicit dialog requiring "REJECT" to be typed in
              setConfirmationDialog({
                confirmationMessage:
                  'If you still want to reject the distribution, enter the word below and click Confirm.',
                confirmationValue: 'REJECT',
                label: 'REJECT',
                onConfirmAction: handleRejectDistribution,
                title: 'Are you sure you want to reject this distribution?',
                warningList: [
                  'Once rejected, this distribution will be considered canceled and cannot be undone.',
                  'This action cannot be undone.',
                ],
              });
              setConfirmationDialogOpen(true);
            }}
            variant="outlined"
          >
            Reject
          </Button>
        ))}
    </Box>
  );

  const dataArray = [
    ['Shipment ID', distributionQuery?.parentShipmentId],
    ['Distribution ID', distributionId],
    ['Inventory Owner', distributionQuery.inventoryOwnerName],
    ['Shipper', distributionQuery.shipperName],
    ['Agreement Code', distributionQuery.agreementCode],
    [
      'Status',
      distributionQuery.status && (
        <Tooltip placement="top" title={distributionStatusTooltips[distributionQuery.status]}>
          <StatusChip status={distributionQuery.status} />
        </Tooltip>
      ),
    ],
    ['Status Details', distributionQuery?.statusDetails],
    [
      'Requested Date',
      distributionQuery.requestedDate ? formatDate(distributionQuery.requestedDate) : null,
    ],
  ];

  const viewAnddataArray = R.concat(dataArray, [
    ['Location', distributionQuery.locationName],
    ['Product', distributionQuery.productName],
    [
      'Volume',
      distributionQuery.volume
        ? `${formatNumberWithoutDecimals(distributionQuery.volume)} bbl `
        : null,
    ],
    [
      'Booked Volume',
      distributionQuery.bookedVolume
        ? `${formatNumberWithoutDecimals(distributionQuery.bookedVolume)} bbl `
        : null,
    ],
    [
      'Reconsignment Control',
      distributionQuery.reconsignmentControl ? 'Any Controlled' : 'Any Uncontrolled',
    ],
    ['Shipment Chain', renderChaining(distributionQuery.chaining)],
  ]);

  const FilteredViewArray = viewAnddataArray.filter(
    ([key]) => key !== 'Shipment ID' && key !== 'Status Details'
  );

  const editArray = R.concat(viewAnddataArray, []);

  const renderEditButtons = () => (
    <Box
      sx={{
        '> *': { flex: 1 },
        '> :nth-of-type(2)': { ml: 4 },
        display: 'flex',
        width: '100%',
      }}
    >
      {hasEditPermissions &&
      hasAcceptRejectPermissions &&
      distributionQuery?.status === OLD_STATUS_CHIP.UNCONFIRMED ? (
        <Button
          onClick={() => {
            // Standard dialog
            setConfirmationDialog({
              confirmationMessage: `Are you sure you would like to accept distribution #${distributionId}?`,
              onConfirmAction: handleAcceptDistribution,
              title: 'Accept distribution?',
            });
            setConfirmationDialogOpen(true);
          }}
          variant="contained"
        >
          Accept
        </Button>
      ) : (
        <>
          <Button onClick={handleEditDistributionClose} variant="outlined">
            Back
          </Button>
          <Button disabled={isInvalid} onClick={handleEditDistributionSave}>
            Save
          </Button>
        </>
      )}
    </Box>
  );

  useEffect(() => {
    setEditState({ ...initialState });
  }, [mode, initialState]);

  return (
    <Drawer
      anchor="right"
      onClose={onClose}
      open={!!distributionId}
      slotProps={{
        paper: { sx: { width: { lg: 380, xs: '100%' } } },
      }}
      variant="temporary"
    >
      <DialogUnsavedChanges open={!!unsavedDialog} {...unsavedDialog} />
      <DialogExplicitConfirmation
        confirmationValue=""
        dialogOpen={confirmationDialogOpen}
        handleCloseDialog={() => setConfirmationDialogOpen(false)}
        onConfirm={() => {
          setConfirmationDialogOpen(false);
          confirmationDialog.onConfirmAction();
        }}
        {...confirmationDialog}
      />
      <DrawerLayoutWrapper
        buttons={isEditMode ? renderEditButtons() : renderViewButtons()}
        onHeaderButtonClick={onClose}
        title={`${isEditMode ? 'Edit' : 'View'} Distribution`}
      >
        <Box sx={{ pt: 3, px: { lg: 3, xs: 2 } }}>
          {isFetching ? (
            <Box textAlign="center" width="100%">
              <CircularProgress size={48} />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                position: 'relative',
                rowGap: { lg: 3, xs: 2 },
              }}
            >
              {(isEditMode ? editArray : FilteredViewArray).map(([label, value]) => (
                <DataDisplayItem key={label} label={label} value={value} />
              ))}
            </Box>
          )}
          {isEditMode && !isFetchingEditQuery && !R.isEmpty(editState) && (
            <>
              <EditDistribution
                availableVolumeExceeded={availableVolumeExceeded}
                distributionData={editDistributionQuery}
                editState={editState}
                isDistributionSuccess={isEditQuerySuccess}
                isFieldsDisabled={shouldDisableFields}
                setEditState={setEditState}
                setValidationMessages={setValidationMessages}
                totalProposedVolume={totalProposedVolume}
                validationMessages={validationMessages}
              />
            </>
          )}
        </Box>
      </DrawerLayoutWrapper>
    </Drawer>
  );
};

DistributionsDrawer.propTypes = {
  distributionId: number,
  mode: bool,
  refreshBatchData: func,
  refreshShipmentData: func.isRequired,
  setSelectedDistribution: func.isRequired,
};

DistributionsDrawer.defaultProps = {
  distributionId: 0,
  mode: DISTRIBUTIONS_DRAWER_MODE.VIEW,
  refreshBatchData: () => {},
};

export default DistributionsDrawer;
